import { memo, useCallback, useRef } from "react";
import styled from "styled-components";

import {
  SkipToStartIcon,
  SkipToEndIcon,
  PauseIcon,
  PlayIcon,
  StepBackwardIcon,
  StepForwardIcon,
  StopIcon,
  SyncIcon,
} from "../../icons/glyphs";
import { TooltipIconButton } from "../IconButton";
import { Spinner } from "../Spinner";
import {
  resolvePlaybackCanStart,
  resolvePlaybackCapabilities,
  resolvePlaybackSeekRange,
  useVideoTimeline,
  type PlaybackTransport,
} from "../Video";
import { formatMediaPlaybackTime, normalizeMediaTimelineCurrent } from "./mediaTimelineMath";
import type { MediaTimelineBarLabels } from "./timelineTypes";
import { useBoundaryFocusRescue } from "./useBoundaryFocusRescue";
import { BUFFERING_INDICATOR_DELAY_MS, useDelayedFlag } from "../Video/useDelayedFlag";

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PlaybackButton = styled(TooltipIconButton)``;

const PlaybackStatus = styled.span`
  display: inline-flex;
  min-width: 1em;
  flex: 0 1 auto;
  align-items: center;
  gap: 0.35rem;
  overflow: hidden;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  white-space: nowrap;
`;

const PlaybackStatusText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TimeLabel = styled.span`
  display: inline-flex;
  align-items: center;
  /* Anchored to the all-static right cluster so contextual frame actions mounting on the left
     never shift the readout the eye tracks during playback. The divider carries the same section
     grammar as the trailing clusters — readout, then zoom/rate controls — and stretches to match
     the buttons' height. */
  align-self: stretch;
  justify-content: flex-end;
  /* Flex layout trims the anonymous text item's leading space, so the gap — not a literal
     space — provides the visual separation while textContent keeps " / " for queries. */
  column-gap: 0.3ch;
  margin-right: 0.2rem;
  padding-right: 0.5rem;
  border-right: 1px solid ${({ theme }) => theme.color.border.divider};
  color: ${({ theme }) => theme.color.text.subtle};
  /* Primary playback readout — one step above SigmaKit's caption/muted so the value the eye
     tracks is legible at a glance; the total duration stays subordinate. */
  font-size: ${({ theme }) => theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const TimeLabelCurrent = styled.span`
  display: inline-grid;
  color: ${({ theme }) => theme.color.text.default};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  /* Reserve only the media's maximum formatted width, with the actual font/weight. The
     invisible template shares a cell with the value so minute digit changes cannot move
     adjacent controls. Tabular digits make its zeroes as wide as any playback value. */
  &::before {
    content: attr(data-width-template);
    visibility: hidden;
    grid-area: 1 / 1;
  }
  > span {
    grid-area: 1 / 1;
    justify-self: end;
  }
`;

export function readMediaTimelineCurrent(transport: PlaybackTransport): number {
  const { currentTimeSeconds, durationSeconds } = transport.getSnapshot();
  return normalizeMediaTimelineCurrent(currentTimeSeconds, durationSeconds);
}

interface MediaTransportControlsProps {
  transport: PlaybackTransport;
  onPrimaryPlaybackToggle?: () => void;
  onPointerAction: (source: HTMLElement) => void;
  stepSeconds: number;
  disabled: boolean;
  hasDuration: boolean;
  seekSupported: boolean;
  text: Required<MediaTimelineBarLabels>;
}

/**
 * Transport controls subscribe only to coarse boundary state. Their handlers read the exact
 * playhead time at activation, so the button/tooltip tree does not reconcile on every frame.
 */
export const MediaTransportControls = memo(function MediaTransportControls({
  transport,
  onPrimaryPlaybackToggle,
  onPointerAction,
  stepSeconds,
  disabled,
  hasDuration,
  seekSupported,
  text,
}: MediaTransportControlsProps) {
  const stepSecondsRef = useRef(stepSeconds);
  stepSecondsRef.current = stepSeconds;
  const { handleGroupActivation } = useBoundaryFocusRescue();
  const playing = useVideoTimeline(transport, (snapshot) => snapshot.playing);
  const playSupported = useVideoTimeline(transport, resolvePlaybackCanStart);
  const live = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackCapabilities(snapshot).live,
  );
  const followingLive = useVideoTimeline(transport, (snapshot) => snapshot.followingLive);
  const status = useVideoTimeline(transport, (snapshot) => snapshot.status);
  const atStart = useVideoTimeline(
    transport,
    (snapshot) =>
      normalizeMediaTimelineCurrent(snapshot.currentTimeSeconds, snapshot.durationSeconds) <=
      resolvePlaybackSeekRange(snapshot).startSeconds,
  );
  const atEnd = useVideoTimeline(transport, (snapshot) => {
    const duration = snapshot.durationSeconds;
    return (
      Number.isFinite(duration) &&
      duration > 0 &&
      normalizeMediaTimelineCurrent(snapshot.currentTimeSeconds, duration) >=
        resolvePlaybackSeekRange(snapshot).endSeconds
    );
  });
  const playbackUnavailable = disabled || !hasDuration;
  const playDisabled = playbackUnavailable || (!playing && !playSupported);
  const seekDisabled = playbackUnavailable || !seekSupported;
  const followSupported = live && followingLive !== undefined && transport.goLive !== undefined;
  // buffering은 재생·seek마다 생기는 짧은 멈춤에 깜빡이지 않게 늦게 보인다. producer 대기(waiting)는
  // 곧바로 보인다.
  const bufferingVisible = useDelayedFlag(
    playing && status === "buffering",
    BUFFERING_INDICATOR_DELAY_MS,
  );
  const waitingLabel =
    playing && status === "waiting" ? text.waiting : bufferingVisible ? text.buffering : null;
  const stepBackward = useCallback(() => {
    transport.seekToSeconds(readMediaTimelineCurrent(transport) - stepSecondsRef.current);
  }, [transport]);
  const stepForward = useCallback(() => {
    transport.seekToSeconds(readMediaTimelineCurrent(transport) + stepSecondsRef.current);
  }, [transport]);
  const toStart = useCallback(() => {
    transport.seekToSeconds(resolvePlaybackSeekRange(transport.getSnapshot()).startSeconds);
  }, [transport]);
  const toEnd = useCallback(() => {
    transport.seekToSeconds(resolvePlaybackSeekRange(transport.getSnapshot()).endSeconds);
  }, [transport]);

  return (
    <>
      <ControlGroup
        role="group"
        aria-label={text.seek}
        onClick={(event) => {
          handleGroupActivation(event);
          const button = (event.target as Element).closest("button");
          if (event.detail > 0 && button) onPointerAction(button);
        }}
      >
        <PlaybackButton
          size="md"
          variant="solid"
          aria-label={playing ? text.pause : text.play}
          tooltip={{ content: playing ? text.pause : text.play, placement: "top" }}
          disabled={playDisabled}
          onClick={onPrimaryPlaybackToggle ?? transport.toggle}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </PlaybackButton>
        {/* 보조 transport는 VideoPlaybackBar와 같은 계약으로 surface를 명시해, 컨테이너 배경 없이도
          한 그룹으로 읽히는 bounded control을 유지한다. */}
        <PlaybackButton
          size="md"
          variant="surface"
          aria-label={text.stop}
          tooltip={{ content: text.stop, placement: "top" }}
          disabled={seekDisabled || (!playing && atStart)}
          onClick={transport.reset}
        >
          <StopIcon />
        </PlaybackButton>
        <PlaybackButton
          size="md"
          variant="surface"
          aria-label={text.toStart}
          tooltip={{ content: text.toStart, placement: "top" }}
          disabled={seekDisabled || atStart}
          onClick={toStart}
        >
          <SkipToStartIcon />
        </PlaybackButton>
        <PlaybackButton
          size="md"
          variant="surface"
          aria-label={text.stepBackward}
          tooltip={{ content: text.stepBackward, placement: "top" }}
          disabled={seekDisabled || atStart}
          onClick={stepBackward}
        >
          <StepBackwardIcon />
        </PlaybackButton>
        <PlaybackButton
          size="md"
          variant="surface"
          aria-label={text.stepForward}
          tooltip={{ content: text.stepForward, placement: "top" }}
          disabled={seekDisabled || atEnd}
          onClick={stepForward}
        >
          <StepForwardIcon />
        </PlaybackButton>
        <PlaybackButton
          size="md"
          variant="surface"
          aria-label={text.toEnd}
          tooltip={{ content: text.toEnd, placement: "top" }}
          disabled={seekDisabled || atEnd}
          onClick={toEnd}
        >
          <SkipToEndIcon />
        </PlaybackButton>
        {followSupported ? (
          <PlaybackButton
            size="md"
            variant="surface"
            aria-label={followingLive ? text.live : text.goLive}
            tooltip={{ content: followingLive ? text.live : text.goLive, placement: "top" }}
            aria-current={followingLive ? "true" : undefined}
            disabled={playbackUnavailable}
            onClick={transport.goLive}
          >
            <SyncIcon />
          </PlaybackButton>
        ) : null}
      </ControlGroup>
      {waitingLabel ? (
        <PlaybackStatus role="status" aria-live="polite" data-playback-status={status}>
          <Spinner size="sm" />
          <PlaybackStatusText>{waitingLabel}</PlaybackStatusText>
        </PlaybackStatus>
      ) : null}
    </>
  );
});

interface TimelineTimeLabelProps {
  transport: PlaybackTransport;
  durationSeconds: number;
}

export const TimelineTimeLabel = memo(function TimelineTimeLabel({
  transport,
  durationSeconds,
}: TimelineTimeLabelProps) {
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const current = normalizeMediaTimelineCurrent(currentTimeSeconds, durationSeconds);
  const total = formatMediaPlaybackTime(durationSeconds, durationSeconds);

  return (
    <TimeLabel data-testid="media-timeline-time">
      <TimeLabelCurrent data-width-template={total.replace(/\d/g, "0")}>
        <span>{formatMediaPlaybackTime(current, durationSeconds)}</span>
      </TimeLabelCurrent>
      {" / "}
      {total}
    </TimeLabel>
  );
});
